import { FC, Fragment, SyntheticEvent, useContext, useEffect, useMemo } from 'react';
import common from '../../util/scss/common.module.scss';
import styles from './CreateFamilyAccountForm.module.scss';
import CreateFamilyAccountContext, { ENameType, TFirstPartyUser } from '../../contexts/CreateFamilyAccountContext';
import {
    Button,
    TextField,
    useTheme,
    Icon,
    IconButton,
    Avatar,
    Stack,
    Typography,
    Box,
    InputAdornment,
} from '@mui/material';
import { removeUrlProtocol, getInitials } from '../../util/helpers';
import { ELinks, maxFamilyItems, EFamilyAccountType, ERoutes, EMixpanelEvent } from '../../util/constants';
import AddButton from '../AddButton/AddButton';
import BackCardLayout from '../../layout/BackCardLayout/BackCardLayout';
import GlobalContext from '../../contexts/GlobalContext';
import { useHistory } from 'react-router';
import CoBranding from '../CoBranding/CoBranding';
import { Divider, Link } from '@mui/material';
import PageLayoutV2 from '../../layout/PageLayoutV2/PageLayoutV2';
import ScreenHeader from '../ScreenHeader/ScreenHeader';
import { IconFamily2 } from '../../icons/Icons';
import mixpanel from 'mixpanel-browser';
import PageLayout from '../../layout/PageLayout/PageLayout';

// ======================
// <PrimaryUser/>
// =====================
type TPrimaryUserProps = {
    onCancel?: () => void;
    firstName: string;
    lastName: string;
    email?: string;
};

const PrimaryUser: FC<TPrimaryUserProps> = (props) => {
    const { language, coBranding } = useContext(GlobalContext);
    const { onCancel, firstName, lastName, email } = props;
    const initials = useMemo(() => getInitials(firstName, lastName), [firstName, lastName]);
    const theme = useTheme();

    return (
        <section
            className={`${styles.primaryUser} ${coBranding.brandColor && styles.coBranded}`}
            data-testid="primary-user"
        >
            <header className={styles.header}>
                <h3>{language.createFamilyAccount.primaryUser.title}</h3>
                {onCancel && (
                    <Button variant="text" onClick={onCancel} sx={{ fontSize: '12px' }}>
                        {language.createFamilyAccount.primaryUser.cta}
                    </Button>
                )}
            </header>
            <article className={styles.user}>
                <Avatar sx={{ mr: 2, background: theme.palette.primary.main }}>{initials}</Avatar>
                <div className={styles.details}>
                    <p className={styles.name}>{`${firstName} ${lastName} (${language.common.you})`}</p>
                    <p className={styles.email}>{email}</p>
                </div>
            </article>
            <Divider sx={{ mt: 3, mb: 3 }} />
        </section>
    );
};

// ======================
// <FamilyItem/>
// ======================
type TFamilyItemProps = {
    index: number;
    firstName: string;
    lastName: string;
    firstNameIsValid: boolean;
    lastNameIsValid: boolean;
    firstNameValidationMessage: string;
    lastNameValidationMessage: string;
    onNameChange: (nameType: ENameType, updateIndex: number, newName: string) => void;
    onDelete?: (index: number) => void;
    hasFocus?: boolean;
};

const FamilyItem: FC<TFamilyItemProps> = (props) => {
    const { language } = useContext(GlobalContext);
    const {
        index,
        firstName,
        lastName,
        firstNameIsValid,
        firstNameValidationMessage,
        lastNameIsValid,
        lastNameValidationMessage,
        onNameChange,
        onDelete,
    } = props;

    const handleDelete = () => {
        onDelete(index);
    };

    return (
        <Box component={'article'} data-testid={`family-form-item-${index}`}>
            <Stack direction={'row'} sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography component="h6" variant="subtitle2">
                    {language.createFamilyAccount.itemHeader}
                </Typography>
                {onDelete && (
                    <IconButton onClick={handleDelete}>
                        <Icon>delete</Icon>
                    </IconButton>
                )}
            </Stack>
            <TextField
                label={language.createAccount.form.firstName}
                value={firstName}
                onChange={(event) => onNameChange(ENameType.first, index, event.target.value)}
                error={!firstNameIsValid}
                helperText={!firstNameIsValid && firstNameValidationMessage}
                fullWidth
                sx={{ mb: 1 }}
                size="small"
                autoFocus
            />
            <TextField
                label={language.createAccount.form.lastName}
                value={lastName}
                onChange={(event) => onNameChange(ENameType.last, index, event.target.value)}
                error={!lastNameIsValid}
                helperText={!lastNameIsValid && lastNameValidationMessage}
                fullWidth
                size="small"
            />
        </Box>
    );
};

// =====================
// <CreateForm/>
// =====================
const CreateForm: FC = () => {
    const { language, returnUrlParameter } = useContext(GlobalContext);
    const {
        primaryUser,
        addFamilyItem,
        confirmFamilyAccount,
        confirmConfirmationFamilyAccount,
        familyAccountType,
        familyItems,
        deleteFamilyItem,
        updateFamilyItemName,
    } = useContext(CreateFamilyAccountContext);
    const history = useHistory();
    const theme = useTheme();

    const handleConfirmFamilyAccount = (event: SyntheticEvent) => {
        event.preventDefault();
        if (familyAccountType === EFamilyAccountType.confirmAccount) {
            confirmConfirmationFamilyAccount();
        } else {
            mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_create_account_family_add_done' });
            confirmFamilyAccount();
        }
    };

    const handleChangeEmail = () => {
        mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_create_account_family_add_not_you' });
        history.push({
            pathname: `${ERoutes.login}`,
            search: `?${returnUrlParameter}`,
        });
    };

    const handleAddFamilyItem = (event: SyntheticEvent) => {
        event.preventDefault();
        mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_create_account_family_add_new' });
        addFamilyItem();
    };

    const handleDeleteFamilyItem = (index: number) => {
        mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_create_account_family_add_delete' });
        deleteFamilyItem(index);
    };

    useEffect(() => {
        mixpanel.track(EMixpanelEvent.viewed, { key: 'consumer_create_account_family_add' });
    }, []);

    return (
        <PageLayoutV2>
            <Box sx={{ textAlign: { lg: 'center' } }}>
                <IconFamily2 fillColor={theme.palette.primary.main} />
            </Box>
            <ScreenHeader
                margin={1}
                title={language.createFamilyAccount.header}
                desc={language.createFamilyAccount.desc}
            />
            {'email' in primaryUser && (
                <>
                    <Typography component={'h3'} variant={'subtitle2'} sx={{ my: 2 }}>
                        {language.createFamilyAccount.primaryUser.title}
                    </Typography>

                    <TextField
                        label={language.createAccount.form.email}
                        value={primaryUser.email}
                        fullWidth
                        id="email"
                        size="small"
                        disabled
                        InputProps={
                            'canShowUsername' in primaryUser && !primaryUser.canShowUsername
                                ? undefined
                                : {
                                      endAdornment: (
                                          <InputAdornment position="end">
                                              <Link
                                                  onClick={() => {
                                                      handleChangeEmail();
                                                  }}
                                                  color={theme.palette.action.active}
                                                  sx={{ typography: 'subtitle2', cursor: 'pointer' }}
                                                  data-testid="change-email"
                                              >
                                                  {language.common.edit}
                                              </Link>
                                          </InputAdornment>
                                      ),
                                  }
                        }
                    />
                </>
            )}
            <Divider sx={{ my: 3 }} />
            <form>
                {familyItems.map((item, index) => {
                    if (index === 0) {
                        return (
                            <FamilyItem
                                key={index}
                                index={index}
                                firstName={item.firstName}
                                lastName={item.lastName}
                                firstNameIsValid={item.firstNameIsValid}
                                firstNameValidationMessage={item.firstNameValidationMessage}
                                lastNameIsValid={item.lastNameIsValid}
                                lastNameValidationMessage={item.lastNameValidationMessage}
                                onNameChange={updateFamilyItemName}
                                hasFocus={item.hasFocus}
                            />
                        );
                    } else
                        return (
                            <Fragment key={index}>
                                <Divider sx={{ mt: 3, mb: 2 }} />
                                <FamilyItem
                                    key={index}
                                    index={index}
                                    firstName={item.firstName}
                                    lastName={item.lastName}
                                    firstNameIsValid={item.firstNameIsValid}
                                    firstNameValidationMessage={item.firstNameValidationMessage}
                                    lastNameIsValid={item.lastNameIsValid}
                                    lastNameValidationMessage={item.lastNameValidationMessage}
                                    onNameChange={updateFamilyItemName}
                                    onDelete={handleDeleteFamilyItem}
                                />
                            </Fragment>
                        );
                })}
                {familyItems.length < maxFamilyItems ? (
                    <Link
                        component={'button'}
                        type="button"
                        onClick={handleAddFamilyItem}
                        typography={'subtitle2'}
                        sx={{ mt: 3 }}
                    >
                        {language.createFamilyAccount.addItemCta}
                    </Link>
                ) : (
                    <Typography component={'p'} variant={'body2'} sx={{ mt: 2 }}>
                        {language.createFamilyAccount.maxItems}{' '}
                        <Link href={ELinks.userPortal} target="_blank">{`${removeUrlProtocol(
                            ELinks.userPortal,
                        )}`}</Link>
                    </Typography>
                )}
                <Button
                    variant="contained"
                    type="submit"
                    size="large"
                    onClick={handleConfirmFamilyAccount}
                    fullWidth
                    sx={{ my: 3 }}
                >
                    {language.common.next}
                </Button>
            </form>
        </PageLayoutV2>
    );
};

// =====================
// <ConfirmFamilyFirstForm/>
// =====================
const ConfirmFamilyFirstForm: FC = () => {
    const { language, coBranding } = useContext(GlobalContext);
    const {
        primaryUser,
        addFamilyItem,
        confirmFamilyAccount,
        confirmConfirmationFamilyAccount,
        familyAccountType,
        familyItems,
        deleteFamilyItem,
        updateFamilyItemName,
    } = useContext(CreateFamilyAccountContext);
    const history = useHistory();

    const handleConfirmFamilyAccount = () => {
        if (familyAccountType === EFamilyAccountType.confirmAccount) {
            confirmConfirmationFamilyAccount();
        } else {
            confirmFamilyAccount();
        }
    };

    const goBack = () => history.goBack();

    return (
        <PageLayout showHeader={false}>
            <BackCardLayout>
                {(coBranding.logoUrl || coBranding.name) && (
                    <CoBranding logoUrl={coBranding.logoUrl} name={coBranding.name} />
                )}
                <header>
                    <strong className={common.progressHeader}>{`${language.common.step} 3`}</strong>
                    <h1 className={`${common.pageHeader} ${common.withProgressMinPadding}`}>
                        {language.confirmAccount.family.familyFirstTitle}
                    </h1>
                    <p className={styles.introDesc}>{language.confirmAccount.family.familyFirstDesc}</p>
                </header>
                <PrimaryUser
                    onCancel={goBack}
                    firstName={primaryUser.firstName}
                    lastName={primaryUser.lastName}
                    email={'email' in primaryUser ? primaryUser.email : undefined}
                />
                <form>
                    {familyItems.map((item, index) => {
                        if (index === 0) {
                            return (
                                <FamilyItem
                                    key={index}
                                    index={index}
                                    firstName={item.firstName}
                                    lastName={item.lastName}
                                    firstNameIsValid={item.firstNameIsValid}
                                    firstNameValidationMessage={item.firstNameValidationMessage}
                                    lastNameIsValid={item.lastNameIsValid}
                                    lastNameValidationMessage={item.lastNameValidationMessage}
                                    onNameChange={updateFamilyItemName}
                                    hasFocus={item.hasFocus}
                                />
                            );
                        } else {
                            return (
                                <Fragment key={index}>
                                    <Divider />
                                    <FamilyItem
                                        index={index}
                                        firstName={item.firstName}
                                        lastName={item.lastName}
                                        firstNameIsValid={item.firstNameIsValid}
                                        firstNameValidationMessage={item.firstNameValidationMessage}
                                        lastNameIsValid={item.lastNameIsValid}
                                        lastNameValidationMessage={item.lastNameValidationMessage}
                                        onNameChange={updateFamilyItemName}
                                        onDelete={deleteFamilyItem}
                                        hasFocus={item.hasFocus}
                                    />
                                </Fragment>
                            );
                        }
                    })}
                    {familyItems.length < maxFamilyItems ? (
                        <AddButton label={language.createFamilyAccount.addItemCta} onClick={addFamilyItem} />
                    ) : (
                        <section className={styles.maxItemsDesc}>
                            <p>
                                {language.createFamilyAccount.maxItems}{' '}
                                <Link href={ELinks.userPortal} target="_blank">{`${removeUrlProtocol(
                                    ELinks.userPortal,
                                )}`}</Link>
                            </p>
                        </section>
                    )}
                    <section className={styles.confirmFamilyCta}>
                        <Button variant="contained" type="button" onClick={handleConfirmFamilyAccount} size="large">
                            {language.common.next}
                        </Button>
                    </section>
                </form>
            </BackCardLayout>
        </PageLayout>
    );
};

// =====================
// <CreateFamilyAccountForm/>
// =====================
const CreateFamilyAccountForm: FC = () => {
    const { coBranding } = useContext(GlobalContext);
    const { familyAccountType } = useContext(CreateFamilyAccountContext);

    let formUi: React.ReactElement;
    switch (familyAccountType) {
        case EFamilyAccountType.firstParty:
        case EFamilyAccountType.thirdParty:
            formUi = <CreateForm />;
            break;
        case EFamilyAccountType.confirmAccount:
            formUi = coBranding.isDefaultFamilyAccount ? <ConfirmFamilyFirstForm /> : <CreateForm />;
            break;
        default:
            throw Error('Family Account Type Set Incorrectly');
    }
    return formUi;
};

export default CreateFamilyAccountForm;
